import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { CommunityRow } from '../../components/CommunityRow';
import { CreateFab } from '../../components/CreateFab';
import { PageHeader } from '../../components/PageHeader';
import { PostCard } from '../../components/PostCard';
import { PulseRail } from '../../components/PulseRail';
import { SegmentedTabs } from '../../components/SegmentedTabs';
import { SectionTitle } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { communities } from '../../data/communities';
import { useLocale } from '../../contexts/LocaleContext';
import { useSocial } from '../../contexts/SocialContext';
import { Explore } from '../Explore';
import { useScreenInit } from '../../useScreenInit.js';
type Tab = 'feed' | 'communities' | 'discover';

/**
 * MOB/Community/Home — SOCIAL.
 * Pulse + feed, interest communities, and discovery (search, people, topics,
 * trending) live here, never on Home.
 */
export function CommunityHome({
  initialTab = 'feed'
}: {
  initialTab?: Tab;
}) {
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  const social = useSocial();
  const screenInit = useScreenInit();
  const [tab, setTab] = useState<Tab>(screenInit?.communityTab as Tab | undefined ?? initialTab);
  const mine = communities.filter(community => social.isMember(community.id));
  const others = communities.filter(community => !social.isMember(community.id));
  return <div className="pb-2" data-id="element-2551" data-mp="1fhl2g8taijma">
      <PageHeader title={t('community.title')} subtitle={t('community.subtitle')} action={<button type="button" onClick={() => navigate('/busca')} aria-label={t('search.title')} className="grid h-11 w-11 shrink-0 place-items-center rounded-pill text-deep transition-colors duration-150 ease-brand hover:bg-sand-deep" data-id="element-2553" data-mp="1kbp9st6gwtnw">
            <SearchIcon size={19} data-id="element-2554" data-mp="1y6rljwxzxd8m" />
          </button>} data-id="element-2552" data-mp="1fgs4nxcb74ga" />

      <div className="px-2" data-id="element-2555" data-mp="3ucs9qxl7wc5i">
        <SegmentedTabs label={t('community.title')} value={tab} onChange={setTab} tabs={[{
        id: 'feed',
        label: t('community.tab.feed')
      }, {
        id: 'communities',
        label: t('community.tab.communities')
      }, {
        id: 'discover',
        label: t('community.tab.discover')
      }]} data-id="element-2556" data-mp="3u9wb171jpjg0" />
      </div>

      <div className="mt-3 px-2" data-id="element-2557" data-mp="htao06pq8h6d">
        {tab === 'feed' && <>
            <section data-id="element-2558" data-mp="cupne66kbvhe">
              <SectionTitle data-id="element-2559" data-mp="cvhhtdofbn63">{t('pulse.title')}</SectionTitle>
              <PulseRail pulses={social.pulses} data-id="element-2560" data-mp="qvs92htnb3rs" />
            </section>
            <section className="mt-3" data-id="element-2561" data-mp="lwfmly6orypq">
              <SectionTitle data-id="element-2562" data-mp="lxifucrhed8t">{t('community.feed.posts')}</SectionTitle>
              <ul className="space-y-3" data-id="element-2563" data-mp="2smn5nbvof6zk">
                {social.posts.map(post => <li key={post.id} data-id="element-2564" data-mp="2nntvntflly45">
                    <PostCard post={post} data-id="element-2565" data-mp="2novggpmf1yz2" />
                  </li>)}
              </ul>
            </section>
          </>}

        {tab === 'communities' && <>
            <section data-id="element-2566" data-mp="183t7obftv8o2">
              <SectionTitle data-id="element-2567" data-mp="184cm3i69rexc">{t('community.mine')}</SectionTitle>
              {mine.length === 0 ? <EmptyState title={t('community.none.title')} body={t('community.none.body')} data-id="element-2568" data-mp="ud7lodv2uily" /> : <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2569" data-mp="ke8lljodnvak">
                  {mine.map(community => <CommunityRow key={community.id} community={community} data-id="element-2570" data-mp="pfo537awptqu" />)}
                </ul>}
            </section>
            {others.length > 0 && <section className="mt-3" data-id="element-2571" data-mp="fggvmv7amj90">
                <SectionTitle data-id="element-2572" data-mp="25sw916c8tjna">{t('community.discover')}</SectionTitle>
                <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2573" data-mp="25qidyxhkc7fq">
                  {others.map(community => <CommunityRow key={community.id} community={community} data-id="element-2574" data-mp="1l43s1quc8yrx" />)}
                </ul>
              </section>}
          </>}

        {tab === 'discover' && <Explore embedded data-id="element-2575" data-mp="1b4v0f4my3w8q" />}
      </div>

      <CreateFab data-id="element-2576" data-mp="1b3tbbw93h980" />
    </div>;
}